import React from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { 
  BookOpen, 
  Clock, 
  ArrowRight, 
  ArrowLeft,
  Wrench,
  TrendingUp,
  ShieldCheck,
  FileText,
  Layers,
  Cog,
  Home,
  Target,
  Search,
  Gauge,
  HardHat,
  Droplets,
  Settings,
  DollarSign
} from 'lucide-react';
import { guideCategories } from './GuidesIndex';

// Republish the guides data for category filtering
const allGuides = [
  {
    id: 'foreuse',
    slug: '/guides/comment-choisir-foreuse-geotechnique',
    title: 'Comment choisir une foreuse géotechnique ?',
    subtitle: 'Guide complet 2026',
    description: 'Tout savoir pour choisir la foreuse idéale : 10 critères essentiels, 4 familles de machines, comparatif des marques, prix neuf et occasion.',
    readTime: '12 min',
    icon: Wrench,
    color: 'bg-blue-500',
    tags: ['Foreuse', 'Achat', 'Location', 'Occasion'],
    category: 'materiel-foreuses',
  },
  {
    id: 'etude-geotechnique',
    slug: '/guides/comment-choisir-etude-geotechnique',
    title: 'Comment choisir son étude géotechnique ?',
    subtitle: 'Missions G1 à G5 expliquées',
    description: 'Quelle étude géotechnique choisir pour votre projet ? Guide complet des missions G1, G2, G3, G4, G5 selon la norme NF P 94-500.',
    readTime: '10 min',
    icon: FileText,
    color: 'bg-green-500',
    tags: ['Étude de sol', 'G1', 'G2', 'NF P 94-500'],
    category: 'etudes-fondations',
  },
  {
    id: 'fondations-speciales',
    slug: '/guides/comment-choisir-fondations-speciales',
    title: 'Comment choisir ses fondations spéciales ?',
    subtitle: 'Pieux, micropieux, parois moulées',
    description: 'Pieux, micropieux, parois moulées, colonnes ballastées, jet-grouting : quelle fondation spéciale choisir selon votre sol et votre projet ?',
    readTime: '12 min',
    icon: ShieldCheck,
    color: 'bg-purple-500',
    tags: ['Pieux', 'Micropieux', 'Paroi moulée', 'Jet-grouting'],
    category: 'etudes-fondations',
  },
  {
    id: 'paroi-moulee',
    slug: '/guides/paroi-moulee-technique-realisation',
    title: 'Paroi moulée : technique et réalisation',
    subtitle: 'Benne, hydrofraise, étapes',
    description: 'Tout sur la paroi moulée : étapes de réalisation, benne et hydrofraise, boue bentonite, paramètres de conception et applications.',
    readTime: '14 min',
    icon: Layers,
    color: 'bg-cyan-500',
    tags: ['Paroi moulée', 'Soutènement', 'Benne', 'Hydrofraise'],
    category: 'etudes-fondations',
  },
  {
    id: 'materiel-forage',
    slug: '/guides/materiel-de-forage',
    title: 'Matériel de forage : le guide complet',
    subtitle: 'Machines, outils, instrumentation',
    description: 'Foreuse, tarière, carottier, trépan, tubage, enregistreur de paramètres : le guide complet du matériel de forage géotechnique.',
    readTime: '15 min',
    icon: Cog,
    color: 'bg-orange-500',
    tags: ['Tarière', 'Carottier', 'Instrumentation', 'Outils'],
    category: 'materiel-foreuses',
  },
  {
    id: 'fissures-maison',
    slug: '/guides/fissures-maison-quand-s-inquieter',
    title: 'Fissures maison : quand s\'inquiéter ?',
    subtitle: 'Types, causes, solutions',
    description: 'Fissure sur un mur de votre maison : est-ce grave ? Types de fissures, signaux d\'alerte, causes (sécheresse RGA), solutions et assurance.',
    readTime: '12 min',
    icon: Home,
    color: 'bg-red-500',
    tags: ['Fissures', 'Maison', 'Sécheresse', 'Diagnostic'],
    category: 'etudes-fondations',
  },
  {
    id: 'enregistreur-parametres',
    slug: '/guides/enregistreur-parametres-forage',
    title: 'Enregistreur de paramètres de forage',
    subtitle: 'DAC, contrôle et traçabilité',
    description: 'Tout sur l\'enregistreur de paramètres de forage (EPF) : rôle, paramètres mesurés, contrôle qualité des pieux, conformité NF EN 1536.',
    readTime: '8 min',
    icon: TrendingUp,
    color: 'bg-indigo-500',
    tags: ['DAC', 'Contrôle qualité', 'NF EN 1536', 'Traçabilité'],
    category: 'instrumentation-mesure',
  },
  {
    id: 'micropieux',
    slug: '/guides/micropieux',
    title: 'Micropieux : guide complet',
    subtitle: 'Types, mise en œuvre et reprise en sous-œuvre',
    description: 'Tout sur les micropieux : les 4 types (I à IV), mise en œuvre, reprise en sous-œuvre d\'une maison, normes NF EN 14199 et DTU 13.2.',
    readTime: '10 min',
    icon: Target,
    color: 'bg-cyan-500',
    tags: ['Micropieux', 'Reprise sous-œuvre', 'NF EN 14199', 'Types I-IV'],
    category: 'etudes-fondations',
  },
  {
    id: 'essai-pressiometrique',
    slug: '/guides/essai-pressiometrique-menard',
    title: 'Essai pressiométrique Ménard : EM, pl* et interprétation',
    subtitle: 'Norme NF EN ISO 22476-4',
    description: 'Comprendre l\'essai pressiométrique Ménard : principe, norme NF EN ISO 22476-4, paramètres EM, pl* et pf, lecture de la courbe et dimensionnement des fondations.',
    readTime: '10 min',
    icon: TrendingUp,
    color: 'bg-indigo-500',
    tags: ['Pressiomètre', 'EM', 'pl*', 'NF EN ISO 22476-4'],
    category: 'instrumentation-mesure',
  },
  {
    id: 'injection',
    slug: '/guides/injection-coulis-traitement-de-sol',
    title: 'Injection en géotechnique : coulis et traitement de sol',
    subtitle: 'Mécanismes, produits, résine expansive',
    description: 'Injection de coulis de ciment, comblement, traitement de sol, résine expansive : mécanismes, produits, étapes de chantier et contrôle selon la norme NF EN 12715.',
    readTime: '12 min',
    icon: Layers,
    color: 'bg-teal-500',
    tags: ['Injection', 'Coulis', 'Résine expansive', 'NF EN 12715'],
    category: 'etudes-fondations',
  },
  {
    id: 'foreuse-occasion',
    slug: '/guides/foreuse-occasion-micropieux',
    title: 'Foreuse d\'occasion pour micropieux : guide d\'achat',
    subtitle: 'Critères, documents, pièges à éviter',
    description: 'Acheter une foreuse d\'occasion pour micropieux : critères techniques, points d\'usure, heures de forage, documents (CE, VGP) et pièges à éviter.',
    readTime: '12 min',
    icon: Search,
    color: 'bg-amber-500',
    tags: ['Foreuse occasion', 'Micropieux', 'VGP', 'Guide achat'],
    category: 'materiel-foreuses',
  },
  {
    id: 'pressiometre-choix',
    slug: '/guides/choisir-pressiometre-manuel-automatique',
    title: 'Choisir son pressiomètre : manuel ou automatique ?',
    subtitle: 'Neuf, occasion ou location',
    description: 'Pressiomètre manuel ou auto-contrôlé, neuf, occasion ou location : critères de choix, exigences des marchés, étalonnage et points de vigilance.',
    readTime: '10 min',
    icon: Gauge,
    color: 'bg-violet-500',
    tags: ['Pressiomètre', 'Manuel', 'Automatique', 'Étalonnage'],
    category: 'instrumentation-mesure',
  },
  {
    id: 'metier-foreur',
    slug: '/guides/metier-foreur-sondeur',
    title: 'Foreur ou sondeur : métier, compétences et formations',
    subtitle: 'CACES, AIPR, évolution de carrière',
    description: 'Le métier de foreur et de sondeur en géotechnique : missions, maîtrise des appareils de mesure, certifications CACES et AIPR, formations et évolution de carrière.',
    readTime: '12 min',
    icon: HardHat,
    color: 'bg-emerald-500',
    tags: ['Métier', 'Foreur', 'Sondeur', 'CACES', 'Formation'],
    category: 'materiel-foreuses',
  },
  {
    id: 'prix-forage-eau',
    slug: '/guides/prix-forage-eau',
    title: 'Prix d\'un forage d\'eau en 2026 : combien ça coûte vraiment ?',
    subtitle: 'Budget, démarches, DUPLOS',
    description: 'Prix d\'un forage d\'eau en 2026 : coût au mètre, budget complet, équipements, démarches obligatoires (déclaration mairie, DUPLOS). Le guide pour estimer votre projet.',
    readTime: '8 min',
    icon: Droplets,
    color: 'bg-sky-500',
    tags: ['Forage eau', 'Prix', 'DUPLOS', 'Particuliers'],
    category: 'forage-eau',
  },
  {
    id: 'etalonnage-capteurs',
    slug: '/guides/etalonnage-capteurs-forage',
    title: 'Étalonnage des capteurs de forage : obligations, normes et bonnes pratiques',
    subtitle: 'ISO 17025, traçabilité, certificat',
    description: 'Étalonnage des capteurs de forage et d\'instrumentation géotechnique : obligations, norme ISO 17025, traçabilité métrologique, fréquence et certificat. Le guide complet.',
    readTime: '10 min',
    icon: Settings,
    color: 'bg-slate-600',
    tags: ['Étalonnage', 'ISO 17025', 'Capteurs', 'Métrologie'],
    category: 'instrumentation-mesure',
  },
];

const GuideCategoryPage = () => {
  const { categorySlug } = useParams();
  
  // Find category
  const category = guideCategories.find(c => c.id === categorySlug);
  
  // If category not found, redirect to guides
  if (!category) {
    return <Navigate to="/guides" replace />;
  }
  
  // Filter guides for this category
  const categoryGuides = allGuides.filter(g => g.category === categorySlug);
  
  const CategoryIcon = category.icon;
  
  // Get other categories for navigation
  const otherCategories = guideCategories.filter(c => c.id !== categorySlug && allGuides.some(g => g.category === c.id));
  
  // Schema.org structured data
  const collectionSchema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": `${category.name} | Guides Géotechniques`,
    "description": category.description,
    "url": `https://geots.consulting/guides/categorie/${categorySlug}`,
    "publisher": {
      "@type": "Organization",
      "name": "GEOTS Consulting"
    },
    "hasPart": categoryGuides.map(guide => ({
      "@type": "Article",
      "name": guide.title,
      "url": `https://geots.consulting${guide.slug}`
    }))
  };

  return (
    <>
      <Helmet>
        <title>{category.name} | Guides Géotechniques | GEOTS Consulting</title>
        <meta name="description" content={category.description} />
        <link rel="canonical" href={`https://geots.consulting/guides/categorie/${categorySlug}`} />
        <meta property="og:title" content={`${category.name} | Guides Géotechniques`} />
        <meta property="og:description" content={category.description} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`https://geots.consulting/guides/categorie/${categorySlug}`} />
        <script type="application/ld+json">{JSON.stringify(collectionSchema)}</script>
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        {/* Hero Section */}
        <header className={`${category.color} text-white py-16`}>
          <div className="container mx-auto px-4">
            {/* Breadcrumb */}
            <nav className="mb-6">
              <ol className="flex items-center gap-2 text-white/70 text-sm">
                <li>
                  <Link to="/" className="hover:text-white">Accueil</Link>
                </li>
                <li>/</li>
                <li>
                  <Link to="/guides" className="hover:text-white">Guides</Link>
                </li>
                <li>/</li>
                <li className="text-white font-medium">{category.name}</li>
              </ol>
            </nav>
            
            <div className="max-w-3xl">
              <div className="flex items-center gap-4 mb-6">
                <div className="bg-white/20 p-4 rounded-xl">
                  <CategoryIcon className="w-10 h-10" />
                </div>
                <span className="text-white/80 font-semibold">{categoryGuides.length} guide{categoryGuides.length > 1 ? 's' : ''}</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold mb-6">
                {category.name}
              </h1>
              <p className="text-xl text-white/90">
                {category.description}
              </p>
            </div>
          </div>
        </header>

        <div className="container mx-auto px-4 py-12">
          {/* Back link */}
          <Link 
            to="/guides" 
            className="inline-flex items-center gap-2 text-gray-600 hover:text-[#635BFF] mb-8 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Tous les guides
          </Link>

          {/* Guides Grid */}
          <section className="mb-16">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {categoryGuides.map((guide) => (
                <Link
                  key={guide.id}
                  to={guide.slug}
                  className="group bg-white rounded-2xl border hover:shadow-xl transition-all duration-300 overflow-hidden"
                >
                  {/* Card Header */}
                  <div className={`${guide.color} p-6 text-white relative overflow-hidden`}>
                    <div className="absolute top-0 right-0 opacity-10">
                      <guide.icon className="w-32 h-32 -mr-8 -mt-8" />
                    </div>
                    <div className="relative z-10">
                      <div className="flex items-center gap-2 mb-3">
                        <span className="bg-white/20 text-white text-xs font-bold px-2 py-1 rounded">
                          GUIDE COMPLET
                        </span>
                        <span className="flex items-center gap-1 text-sm text-white/80">
                          <Clock className="w-4 h-4" />
                          {guide.readTime}
                        </span>
                      </div>
                      <h2 className="text-xl font-bold mb-1">{guide.title}</h2>
                      <p className="text-white/80 text-sm">{guide.subtitle}</p>
                    </div>
                  </div>

                  {/* Card Body */}
                  <div className="p-6">
                    <p className="text-gray-600 mb-4 line-clamp-3">
                      {guide.description}
                    </p>
                    <div className="flex flex-wrap gap-2 mb-4">
                      {guide.tags.map((tag, i) => (
                        <span key={i} className="bg-gray-100 text-gray-600 text-xs px-2 py-1 rounded">
                          {tag}
                        </span>
                      ))}
                    </div>
                    <div className="flex items-center text-[#635BFF] font-semibold group-hover:gap-3 transition-all">
                      Lire le guide
                      <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </section>

          {/* Other Categories */}
          {otherCategories.length > 0 && (
            <section>
              <h2 className="text-2xl font-bold text-[#0A2540] mb-6">Autres catégories</h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                {otherCategories.map((cat) => {
                  const CatIcon = cat.icon;
                  const count = allGuides.filter(g => g.category === cat.id).length;
                  return (
                    <Link
                      key={cat.id}
                      to={`/guides/categorie/${cat.id}`}
                      className="flex items-center gap-3 p-4 bg-white rounded-xl border hover:shadow-md transition-all group"
                    >
                      <div className={`${cat.color} p-2 rounded-lg text-white`}>
                        <CatIcon className="w-5 h-5" />
                      </div>
                      <div>
                        <p className="font-medium text-[#0A2540] group-hover:text-[#635BFF] transition-colors">
                          {cat.name}
                        </p>
                        <p className="text-sm text-gray-500">{count} guide{count > 1 ? 's' : ''}</p>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </section>
          )}
        </div>
      </div>
    </>
  );
};

export default GuideCategoryPage;
